import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { Card } from '../../components/ui/Card';
import { SkeletonRow } from '../../components/ui/Loader';
import { api } from '../../lib/api';
import { useDocumentTitle } from '../../lib/title';
import { useSession } from '../../stores/session';

export interface Announcement {
  id: number;
  title: string;
  body: string;
  version: string | null;
  published_at: string;
  author: string | null;
}

/**
 * What has changed.
 *
 * ## Public, and that is the point
 *
 * A changelog is an argument for the platform rather than a members' area.
 * Somebody deciding whether this is worth an account is exactly who benefits
 * from seeing that it is being worked on, and there is nothing in a release
 * note worth keeping from a stranger.
 *
 * ## Reading it is what marks it read
 *
 * The page tells the server it was opened. No "mark all as read" button,
 * because pressing one on a changelog you have just finished reading is a
 * chore invented by the software.
 *
 * Only when signed in - a stranger has nothing to mark, and the dot they never
 * saw does not need clearing.
 */
export function WhatsNewPage() {
  useDocumentTitle("What's new");

  const user = useSession((state) => state.user);
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ['announcements'],
    queryFn: () => api.get<{ items: Announcement[]; unread: number }>('/announcements'),
  });

  const seen = useMutation({
    mutationFn: () => api.post('/me/announcements/seen'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['announcements'] }),
  });

  /*
   * Fired once the list has actually arrived, not on mount.
   *
   * Marking read before the page has anything on it would clear the dot for
   * somebody whose connection dropped between opening the page and seeing it -
   * they would be told there was something new, find nothing, and never be
   * told again.
   */
  const arrived = data !== undefined;

  useEffect(() => {
    if (arrived && user !== null) seen.mutate();
    // `seen` is a stable mutation object; including it would re-fire on every
    // render of the hook it belongs to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrived, user]);

  const items = data?.items ?? [];

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6 px-6 py-10">
      <header className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight text-ink">What's new</h1>
        <p className="text-sm leading-relaxed text-soft">
          Everything we have shipped, newest first.
        </p>
      </header>

      {isPending && (
        <div className="flex flex-col gap-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      )}

      {data !== undefined && items.length === 0 && (
        <Card className="p-6">
          <p className="text-sm text-soft">Nothing yet. The first update will appear here.</p>
        </Card>
      )}

      <ol className="flex flex-col gap-4" data-announcements>
        {items.map((entry) => (
          <li key={entry.id}>
            <Card className="flex flex-col gap-3 p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <h2 className="text-lg font-bold tracking-tight text-ink">{entry.title}</h2>

                <span className="meta-caps shrink-0 text-faint">
                  {new Date(entry.published_at).toLocaleDateString(undefined, {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                  {entry.version !== null && ` · ${entry.version}`}
                </span>
              </div>

              <Body text={entry.body} />
            </Card>
          </li>
        ))}
      </ol>
    </div>
  );
}

/**
 * Release-note text, rendered without a Markdown parser.
 *
 * There is no Markdown on this site, and adding a renderer to publish release
 * notes would be a parser, a sanitiser and an XSS surface in exchange for bold
 * text. Two conventions cover what a changelog actually needs - a blank line
 * starts a paragraph, a leading hyphen is a bullet - and anything else renders
 * as the characters somebody typed rather than as broken markup.
 *
 * Everything here goes through React as text, so there is no path from a
 * release note to executable HTML at all.
 */
export function Body({ text }: { text: string }) {
  const blocks = text.split(/\n{2,}/).filter((block) => block.trim() !== '');

  return (
    <div className="flex flex-col gap-3">
      {blocks.map((block, index) => {
        const lines = block.split('\n');
        const bullets = lines.every((line) => line.trimStart().startsWith('- '));

        if (bullets) {
          return (
            <ul key={index} className="flex flex-col gap-1.5 border-l border-line pl-4">
              {lines.map((line, i) => (
                <li key={i} className="text-[13px] leading-relaxed text-soft">
                  {line.trimStart().slice(2)}
                </li>
              ))}
            </ul>
          );
        }

        return (
          <p key={index} className="text-pretty text-sm leading-relaxed text-soft">
            {block}
          </p>
        );
      })}
    </div>
  );
}
